import React, { useEffect, useState } from "react";
import styled, { withTheme } from "styled-components";
import { decimalPlaces, roundMealValue } from "../../../../helpers/mealsHelper";
import { useTranslation } from "react-i18next";
import { UNIT } from "../../../../modules/constants";

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-right: 80px;
  margin-top: 16px;
`;

const Icon = styled.i`
  color: ${(props) => props.color || "#000000"};
  margin-right: 5px;
`;

const Percentage = styled.input`
  max-width: 55px;
  text-align: center;
  font-weight: 600 !important;
  height: auto !important;
  padding: 0 !important;
  font-size: 15px !important;

  &:hover,
  &:focus {
    border-bottom: 1px solid ${(props) => props.theme?.darkPurple};
  }
`;

const Grams = styled.span`
  font-weight: 600;
  font-size: 15px;
  margin-right: 5px;
`;

const Info = styled.span`
  font-size: 12px;
  font-weight: 400;
  margin-top: 5px;
  color: ${(props) => props.theme?.blackLite};
`;

const Separator = styled.span`
  margin-left: 10px;
  margin-right: 5px;
`;

const Title = styled.span`
  color: ${(props) => props.color || "#000000"};
  font-weight: bold;
  font-size: 15px;
  margin-left: 5px;
`;

const StyledError = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme?.cardinal};
  text-transform: uppercase;
`;

const TargetInput = (props) => {
  const {
    defaultValue,
    onChange,
    calories,
    kCalGramsRelation,
    className,
    info,
    color,
    title,
  } = props;

  const { t } = useTranslation();

  const [percentage, setPercentage] = useState("");
  const [grams, setGrams] = useState("");
  const [inputError, setInputError] = useState(false);

  useEffect(() => {
    if (defaultValue && percentage === "0%") {
      setPercentage(`${defaultValue}${UNIT.PERCENTAGE}`);
    }
  }, [defaultValue, percentage]);

  const inputChanged = (e) => {
    const tempValue = e.target.value;

    if (
      tempValue === "" ||
      /^(100(\.00?)?|[1-9]?\d(\.\d\d?)?)$/.test(tempValue)
    ) {
      setPercentage(tempValue);
    }
  };

  const onInputFocus = () => {
    setPercentage((prevState) => prevState.replace("%", ""));
  };

  const setPercentageUnit = () => {
    setPercentage((prevState) =>
      prevState === "" ? "" : `${prevState}${UNIT.PERCENTAGE}`
    );
  };

  const onInputBlur = () => {
    setPercentageUnit();
    setInputError(percentage === "");
  };

  useEffect(() => {
    setPercentageUnit();
  }, []);

  useEffect(() => {
    const percentageNumber = Number(percentage.replace("%", ""));
    const gramsNumber = roundMealValue(grams.replace("g", ""));

    onChange(percentageNumber, gramsNumber);
  }, [percentage, onChange, grams]);

  useEffect(() => {
    if (percentage && calories && kCalGramsRelation) {
      const percentageNumber = Number(percentage.replace("%", ""));

      const tempGrams = `${Number(
        roundMealValue(
          (Number(calories) * (percentageNumber / 100)) / kCalGramsRelation
        )
      ).toFixed(decimalPlaces)}g`;
      setGrams(tempGrams);

      const gramsNumber = roundMealValue(tempGrams.replace("g", ""));

      onChange(percentageNumber, gramsNumber);
    } else {
      setGrams("0.0g");
    }
  }, [calories, kCalGramsRelation, onChange, percentage]);

  return (
    <>
      <div className={`d-flex flex-column ${className}`}>
        <Row>
          <Icon color={color} className="fas fa-flag-checkered"></Icon>
          <Percentage
            data-testid={props["data-testid"] || "percentage-input"}
            type="text"
            className="form-control"
            value={percentage}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            onChange={inputChanged}
          />
          <Separator className="text">/</Separator>
          <Grams data-testid="grams-value">{grams}</Grams>
          {t("appText.from")}
          <Title color={color}>{title}</Title>
        </Row>
        {info && <Info>{info}</Info>}
      </div>
      <StyledError>
        {inputError && <span>{t("errorText.fieldRequired")}</span>}
      </StyledError>
    </>
  );
};

TargetInput.defaultProps = {
  className: "",
};

export default withTheme(TargetInput);
